/**
 * Route paths that are referenced from more than one place (route tables, guards, redirects).
 *
 * Hand-typing these strings in a guard is how "/System/LicenseSatus" silently disabled the
 * licence guard allowlist — keep the route definition and every consumer on the same constant.
 *
 * Paths are stored without a leading slash, matching the `path` property of a `Route`.
 */
export const LICENSE_STATUS_PATH = "System/LicenseStatus";
export const LICENSE_REQUIRED_PATH = "LicenseRequired";

/**
 * Every collection whose members are addressed as `<collection>/:id[/<SubPage>]`.
 *
 * Detail pages hang off the collection they belong to rather than a parallel `<Entity>Details`
 * prefix, so this list doubles as the map of the app's entity URL space. `legacy-routes.spec.ts`
 * fails if a route file introduces an entity prefix that is not listed here, or reintroduces the old
 * `<SubPage>/:id` ordering.
 *
 * Order is not significant — it is a registry, not a match list. Kept alphabetical so additions are
 * easy to place.
 */
export const DETAIL_ROUTE_PREFIXES = [
  "AssetGroups",
  "Assets",
  // Asset groups and asset types are mounted under both sections, so both spellings own an id.
  "Assets/Groups",
  "Assets/Types",
  "Accounts",
  "CarSharing/Garages",
  "Devices",
  "Devices/AssetTypes",
  "Devices/Changes",
  "Devices/CustomCommands",
  "Devices/DeviceTypes",
  "Devices/SensorTemplates",
  "DriverGroups",
  "Drivers",
  "Fuel",
  "GeofenceGroups",
  "Geofences",
  "Home/Grid/Templates",
  "Logistics/ActivityCategories",
  "Logistics/ActivityTypes",
  "Logistics/Destinations",
  "Logistics/Routes",
  "Logistics/TaskTypes",
  "Logistics/Templates",
  "Maintenances/ServiceItems",
  "Maintenances/Tyres",
  "Maintenances/WorkOrders",
  "Notifications/Log",
  "Notifications/TriggerSchedules",
  "Notifications/Triggers",
  "Notifications/UserContactGroups",
  "Notifications/UserContacts",
  "Projects",
  "Reporting/Executions",
  "Reporting/Subscriptions",
  "Resellers",
  "Settings/ClientAnnouncements",
  "Settings/ServiceAnnouncements",
  "System/AuditLog",
  "System/Batch",
  "System/ErrorLog",
  "System/HierarchyGroups",
  "System/Tags",
  "System/WebhookEndpoints",
  "Tags",
  "Trailers",
  "Users",
  "Users/UserRoles",
  "Warehouses",
] as const;

/**
 * A collection prefix that owns an `:id` position.
 *
 * Typing {@link detailLink} against this turns a mistyped or stale prefix into a compile error,
 * which is what makes the registry above load-bearing rather than documentation.
 */
export type DetailRoutePrefix = (typeof DETAIL_ROUTE_PREFIXES)[number];

/**
 * Builds a `routerLink` command array for a detail page.
 *
 * Prefer this over interpolating a path string: `["/Devices", id, "Trips"]` cannot silently regress
 * to the old `<SubPage>/:id` ordering, the array form leaves the id encoding to the router, and the
 * prefix is checked against {@link DETAIL_ROUTE_PREFIXES} at compile time.
 *
 * Nested ids are just further segments — a warehouse zone is
 * `detailLink("Warehouses", warehouseId, "Zones", zoneId)`.
 *
 * @example detailLink("Devices", deviceId, "Trips") // ['/Devices', '251085', 'Trips']
 */
export function detailLink(prefix: DetailRoutePrefix, id: string | number, ...subPage: (string | number)[]): (string | number)[] {
  return [`/${prefix}`, id, ...subPage];
}
